/**
 * Business Hours Calendar Integration Hook
 * 営業時間とカレンダー連携フック
 * 
 * Week 3: 営業時間とReservationsカレンダーの統合
 */

import { useState, useEffect, useMemo } from 'react';
import { businessHoursService } from '../services/businessHoursService';
import {
  ResolvedSchedule,
} from '../types/businessHours';

interface CalendarBusinessHours {
  date: string;
  isOpen: boolean;
  timeSlots: { start: string; end: string }[];
  breaks: { start: string; end: string; name: string }[];
  isHoliday: boolean;
  holidayReason?: string;
  isSpecialDay: boolean;
  specialReason?: string;
  maxCapacity?: number;
  restrictions?: string[];
}

interface BusinessHoursCalendarData {
  businessHours: CalendarBusinessHours[];
  specialDays: CalendarBusinessHours[];
  holidays: CalendarBusinessHours[];
  loading: boolean;
  error: string | null;
}

export const useBusinessHoursCalendar = (startDate: string, endDate: string) => {
  const [data, setData] = useState<BusinessHoursCalendarData>({
    businessHours: [],
    specialDays: [],
    holidays: [],
    loading: true,
    error: null,
  });

  // 営業時間データの取得と変換
  useEffect(() => {
    const loadBusinessHoursData = async () => {
      try {
        setData(prev => ({ ...prev, loading: true, error: null }));

        // BusinessHoursServiceを初期化
        await businessHoursService.initialize();

        // 期間内の営業時間を解決
        const schedules = businessHoursService.resolveScheduleForRange(startDate, endDate);
        
        // カレンダー用データに変換
        const calendarData = schedules.map(schedule => convertToCalendarFormat(schedule));
        
        // 種類別に分類
        const businessHours = calendarData.filter(d => d.source === 'default');
        const specialDays = calendarData.filter(d => d.source === 'special_day');
        const holidays = calendarData.filter(d => d.source === 'holiday');

        setData({
          businessHours: businessHours.map(d => d.data),
          specialDays: specialDays.map(d => d.data),
          holidays: holidays.map(d => d.data),
          loading: false,
          error: null,
        });

      } catch (error) {
        console.error('Failed to load business hours data:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: '営業時間データの取得に失敗しました',
        }));
      }
    };

    if (startDate && endDate) {
      loadBusinessHoursData();
    }
  }, [startDate, endDate]);

  // ResolvedScheduleをカレンダー用データに変換
  const convertToCalendarFormat = (resolved: ResolvedSchedule) => {
    const { date, schedule, source, reason } = resolved;
    
    const calendarHours: CalendarBusinessHours = {
      date,
      isOpen: schedule.isOpen,
      timeSlots: schedule.timeSlots || [],
      breaks: schedule.breaks?.map(b => ({
        start: b.start,
        end: b.end,
        name: b.name,
      })) || [],
      isHoliday: !!schedule.isHoliday,
      holidayReason: schedule.holidayReason,
      isSpecialDay: source === 'special_day',
      specialReason: reason,
      maxCapacity: schedule.maxCapacity,
      restrictions: [],
    };

    return { data: calendarHours, source };
  };

  // FullCalendar用のイベントデータを生成
  const businessHoursEvents = useMemo(() => {
    const events: unknown[] = [];
    
    // 営業時間を背景イベントとして表示
    data.businessHours.forEach(hours => {
      hours.timeSlots.forEach(slot => {
        events.push({
          id: `business-hours-${hours.date}-${slot.start}`,
          title: '営業時間',
          start: `${hours.date}T${slot.start}`,
          end: `${hours.date}T${slot.end}`,
          display: 'background',
          backgroundColor: '#e8f5e8',
          classNames: ['business-hours'],
        });
      });

      // 休憩時間を表示
      hours.breaks.forEach(breakTime => {
        events.push({
          id: `break-${hours.date}-${breakTime.start}`,
          title: breakTime.name || '休憩時間',
          start: `${hours.date}T${breakTime.start}`,
          end: `${hours.date}T${breakTime.end}`,
          display: 'background',
          backgroundColor: '#fff3cd',
          classNames: ['break-time'],
        });
      });
    });

    // 特別営業日を表示
    data.specialDays.forEach(special => {
      special.timeSlots.forEach(slot => {
        events.push({
          id: `special-${special.date}-${slot.start}`,
          title: `特別営業: ${special.specialReason}`,
          start: `${special.date}T${slot.start}`,
          end: `${special.date}T${slot.end}`,
          display: 'background',
          backgroundColor: '#e1f5fe',
          classNames: ['special-day'],
        });
      });
    });

    // 休業日を表示
    data.holidays.forEach(holiday => {
      if (!holiday.isOpen) {
        events.push({
          id: `holiday-${holiday.date}`,
          title: `休業: ${holiday.holidayReason}`,
          start: holiday.date,
          display: 'background',
          backgroundColor: '#ffebee',
          classNames: ['holiday'],
          allDay: true,
        });
      }
    });

    return events;
  }, [data]);

  // 指定日時が営業時間内かチェック
  const isOpenAt = (date: string, time: string): boolean => {
    return businessHoursService.isOpenAtTime(date, time);
  };

  // 指定日の営業時間を取得
  const getBusinessHoursForDate = (date: string): CalendarBusinessHours | null => {
    return [...data.businessHours, ...data.specialDays].find(h => h.date === date) || null;
  };

  // 次の営業日を取得
  const getNextBusinessDay = (fromDate: string): string | null => {
    return businessHoursService.getNextOperatingDay(fromDate);
  };

  // 営業時間の制約をチェック
  const validateReservationTime = (date: string, startTime: string, endTime: string) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 営業時間内かチェック
    if (!isOpenAt(date, startTime)) {
      errors.push('開始時間が営業時間外です');
    }
    
    if (!isOpenAt(date, endTime)) {
      errors.push('終了時間が営業時間外です');
    }

    // 休憩時間と重複チェック
    const dayHours = getBusinessHoursForDate(date);
    if (dayHours) {
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);

      dayHours.breaks.forEach(breakTime => {
        const breakStart = timeToMinutes(breakTime.start);
        const breakEnd = timeToMinutes(breakTime.end);

        if ((startMinutes < breakEnd && endMinutes > breakStart)) {
          warnings.push(`${breakTime.name}と重複しています`);
        }
      });
    }

    return { errors, warnings, isValid: errors.length === 0 };
  };

  // 時間を分に変換
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // FullCalendar用の営業時間設定
  const businessHoursConfig = useMemo(() => {
    // 基本営業時間の設定
    const config = data.businessHours.reduce((acc: { daysOfWeek: number[]; startTime: string; endTime: string; }[], hours) => {
      if (hours.isOpen) {
        hours.timeSlots.forEach(slot => {
          acc.push({
            daysOfWeek: [new Date(hours.date).getDay()],
            startTime: slot.start,
            endTime: slot.end,
          });
        });
      }
      return acc;
    }, []);

    return config;
  }, [data.businessHours]);

  return {
    ...data,
    businessHoursEvents,
    businessHoursConfig,
    isOpenAt,
    getBusinessHoursForDate,
    getNextBusinessDay,
    validateReservationTime,
    refresh: () => {
      // データを再読み込み
      setData(prev => ({ ...prev, loading: true }));
    },
  };
};

export default useBusinessHoursCalendar;